import { api, AxiosRequestConfigWithMetadata } from '../../services/api.service'

// ── Request types ──────────────────────────────────────────────────────────────
export interface CreateServiceRequestDto {
  subject: string
  category: string      // '1' plumbing, '2' electrical, '3' heating, '4' appliance, '5' other
  description: string   // JSON string: { requestType, ...fields }
  urgency: string       // '1' emergency, '2' high, '3' medium, '4' low
  contractNumber: string
  contract_id: string
  contact_id: string
  name: string
  phone: string
  email: string
  imageReferences?: string  // JSON array string: '["uuid1","uuid2"]'
}

// ── Response types ─────────────────────────────────────────────────────────────
export interface CreateServiceRequestResponse {
  code?: string
  status?: string
  description?: string
  sr_number?: string
  ticket_number?: string
  success?: boolean
}

export interface ServiceRequestItem {
  code: string
  ticketnr?: string
  contract_number: string
  create_date: string
  description: string
  main_issue: string
  status: string
  subject: string
  urgency: string
  contacted_via: string
  zFeedbackTenantAppKTU?: string
  user_comment: { comment: string; date: string; sr_number: string }[]
  messageCount?: number
}

export interface ServiceRequestFile {
  sr_number: string
  att_datas: string
  att_type?: string
  file_name: string
  uploading?: boolean
}

// ── API ────────────────────────────────────────────────────────────────────────
class RepairAndInquiriesApi {
  async createServiceRequest(dto: CreateServiceRequestDto, attachments: File[]): Promise<CreateServiceRequestResponse> {
    const formData = new FormData()
    Object.entries(dto).forEach(([key, val]) => formData.append(key, String(val)))
    attachments.forEach(file => formData.append('files[]', file))
    const res = await api.post('/api/service-request', formData, {
      timeout: 500000,
      functionName: 'repairAndInquiries_create',
      headers: { 'Content-Type': undefined },
    } as AxiosRequestConfigWithMetadata)
    return res.data
  }

  async getByContractsList(contractsList: string, status: number, forceBtpRequest = true, page = 0, pageSize = 10): Promise<ServiceRequestItem[]> {
    const res = await api.get('/api/service-request/get-requests-by-contracts-list', {
      params: { contractsList, status, forceBtpRequest, page, pageSize },
      functionName: 'repairAndInquiries_getList',
    } as AxiosRequestConfigWithMetadata)
    return res.data
  }

  async getFiles(srNumber: string): Promise<ServiceRequestFile[]> {
    const res = await api.get(`/api/service-request/files/${srNumber}`, {
      functionName: 'repairAndInquiries_getFiles',
    } as AxiosRequestConfigWithMetadata)
    return res.data
  }

  async getTicket(srNumber: string): Promise<ServiceRequestFile> {
    const res = await api.get(`/api/service-request/ticket/${srNumber}`, {
      functionName: 'repairAndInquiries_getTicket',
    } as AxiosRequestConfigWithMetadata)
    return res.data
  }

  async analyzeImage(file: File, category: string, imageReference: string, description?: string): Promise<any> {
    const formData = new FormData()
    formData.append('files', file)
    formData.append('category', category)
    formData.append('imageReference', imageReference)
    if (description) formData.append('description', description)
    const res = await api.post('/api/service-request/analyze-image', formData, {
      timeout: 60000,
      functionName: 'repairAndInquiries_analyzeImage',
      headers: { 'Content-Type': undefined },
    } as AxiosRequestConfigWithMetadata)
    return res.data
  }

  async deleteImageAnalysis(imageReference: string): Promise<{ deleted: boolean }> {
    const res = await api.delete(`/api/service-request/image-analysis/${imageReference}`, {
      functionName: 'repairAndInquiries_deleteImageAnalysis',
    } as AxiosRequestConfigWithMetadata)
    return res.data
  }
}

export const repairAndInquiriesApi = new RepairAndInquiriesApi()
